import {
  Avatar,
  PageSpinner,
  PhoneNumberField,
  useNotifications,
} from '@armhr/ui';
import { Add, ArrowBack, Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import AddContactDialog from '../../components/admin/team-contacts/AddContactDialog';
import CompanyTeamSection from '../../components/dashboard/CompanyTeamSection';
import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../utils/paths';

let nextTempId = -1;
const getNextTempId = () => nextTempId--;

const AdminTeamContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const api = useApi();
  const { user, company } = useUser();
  const { showNotification } = useNotifications();

  const { data: employees } = useApiData((api) => api.company.getEmployees());
  const { data: initialConfig, loading } = useApiData((api) =>
    api.admin.getTeamContacts()
  );
  const { data: supportContacts } = useApiData((api) =>
    api.profiles.getSupportContacts()
  );

  const [sectionTitle, setSectionTitle] = useState('');
  const [sectionMessage, setSectionMessage] = useState('');
  const [contacts, setContacts] = useState<CompanyTeamContact[]>([]);
  const [saving, setSaving] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate(-1);
      return;
    }
  }, [user?.is_admin, navigate]);

  useEffect(() => {
    if (initialConfig) {
      setSectionTitle(
        initialConfig.section_title || `Your ${company?.name || ''} Team`
      );
      setSectionMessage(initialConfig.section_message || '');
      setContacts(initialConfig.contacts || []);
    } else if (initialConfig === null && !loading) {
      setSectionTitle(`Your ${company?.name || ''} Team`);
    }
  }, [initialConfig, loading, company?.name]);

  const previewConfig = useMemo(
    (): CompanyTeamFullConfig => ({
      section_title: sectionTitle || `Your ${company?.name || 'Company'} Team`,
      section_message: sectionMessage,
      contacts: contacts.map((c, idx) => ({
        ...c,
        id: c.id ?? idx,
        display_order: idx,
      })),
    }),
    [sectionTitle, sectionMessage, contacts, company?.name]
  );

  const buildPayload = useCallback((): CompanyTeamFullConfig => {
    return {
      section_title: sectionTitle,
      section_message: sectionMessage,
      contacts: contacts.map((c, idx) => ({
        ...c,
        display_order: idx,
      })),
    };
  }, [sectionTitle, sectionMessage, contacts]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      const response = await api.admin.saveTeamContacts(payload);
      if (response.results) {
        setContacts(response.results.contacts || []);
        setSectionTitle(response.results.section_title || sectionTitle);
        setSectionMessage(response.results.section_message || sectionMessage);
      }
      showNotification({
        message: 'Team contacts saved successfully.',
        severity: 'success',
      });
    } catch {
      showNotification({
        message: 'Failed to save team contacts. Please try again.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddContact = (contactData: {
    name: string;
    email: string;
    phone: string;
    title: string;
    department: string;
  }) => {
    const newContact: CompanyTeamContact = {
      id: getNextTempId(),
      name: contactData.name,
      email: contactData.email || null,
      phone: contactData.phone || null,
      title: contactData.title || null,
      department: contactData.department || null,
      display_order: contacts.length,
    };
    setContacts((prev) => [...prev, newContact]);
  };

  const handleRemoveContact = (contactId: number | null) => {
    setContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const handleUpdateContact = (
    contactId: number | null,
    field: keyof CompanyTeamContact,
    value: string
  ) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === contactId ? { ...c, [field]: value } : c))
    );
  };

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <Box>
      <Helmet>
        <title>Team Contacts | Armhr</title>
      </Helmet>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(paths.adminCompanyConfig)}
          sx={{ textTransform: 'none' }}
        >
          Company Settings
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h2">Team Contacts</Typography>
          <Typography variant="body1" color="text.secondary">
            Configure the contact list your employees see on their dashboard.
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={saving}
          >
            Save
          </LoadingButton>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Left column -- Form */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Section Title
            </Typography>
            <TextField
              fullWidth
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder={`Your ${company?.name || 'Company'} Team`}
              size="small"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              This replaces &quot;Your Armhr team&quot; for your employees.
            </Typography>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
              Section Message
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={sectionMessage}
              onChange={(e) => setSectionMessage(e.target.value)}
              placeholder="For Payroll or HR questions, please reach out to your company contacts below first."
              size="small"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              Shown below the section title to direct employees.
            </Typography>
          </Card>

          <Card sx={{ p: 3, mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Contacts
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={() => setAddContactOpen(true)}
                sx={{ ml: 'auto', textTransform: 'none' }}
              >
                Add Contact
              </Button>
            </Box>

            {contacts.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No contacts yet. Add your first team contact to get started.
              </Typography>
            )}

            {contacts.map((contact, index) => (
              <React.Fragment key={contact.id}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{ width: 36, height: 36, mt: 0.5 }}
                    name={contact.name}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <TextField
                        label="Name"
                        value={contact.name}
                        onChange={(e) =>
                          handleUpdateContact(
                            contact.id,
                            'name',
                            e.target.value
                          )
                        }
                        size="small"
                        sx={{ flex: '1 1 180px' }}
                      />
                      <TextField
                        label="Department"
                        value={contact.department || ''}
                        onChange={(e) =>
                          handleUpdateContact(
                            contact.id,
                            'department',
                            e.target.value
                          )
                        }
                        size="small"
                        placeholder="e.g., Payroll, HR, Benefits"
                        sx={{ flex: '1 1 160px' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <TextField
                        label="Email"
                        value={contact.email || ''}
                        onChange={(e) =>
                          handleUpdateContact(
                            contact.id,
                            'email',
                            e.target.value
                          )
                        }
                        size="small"
                        type="email"
                        sx={{ flex: '1 1 180px' }}
                      />
                      <PhoneNumberField
                        label="Phone"
                        name={`phone-${contact.id}`}
                        value={contact.phone || ''}
                        onChange={(e) =>
                          handleUpdateContact(
                            contact.id,
                            'phone',
                            e.target.value
                          )
                        }
                        size="small"
                        sx={{ flex: '1 1 140px' }}
                      />
                      <TextField
                        label="Title / Role"
                        value={contact.title || ''}
                        onChange={(e) =>
                          handleUpdateContact(
                            contact.id,
                            'title',
                            e.target.value
                          )
                        }
                        size="small"
                        sx={{ flex: '1 1 160px' }}
                      />
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveContact(contact.id)}
                    sx={{ mt: 0.5 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </React.Fragment>
            ))}
          </Card>
        </Grid>

        {/* Right column -- Live Preview */}
        <Grid item xs={12} lg={5}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1, display: 'block' }}
            >
              Employee Dashboard Preview
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: '#f8f9fb',
                p: 2,
              }}
            >
              <CompanyTeamSection
                companyTeam={previewConfig}
                armhrContacts={supportContacts || []}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <AddContactDialog
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        onAdd={(contactData) => {
          handleAddContact(contactData);
          setAddContactOpen(false);
        }}
        employees={employees || []}
        departmentCodes={company?.codes?.department_codes}
      />
    </Box>
  );
};

export default AdminTeamContactsPage;
